import type { ThemeMode } from '@kyumo/shared-ui';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const THEME_OPTIONS: {
  mode: ThemeMode;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}[] = [
  { mode: 'light', label: 'ライト', icon: Sun },
  { mode: 'dark', label: 'ダーク', icon: Moon },
  { mode: 'system', label: 'システム', icon: Monitor },
];

function CurrentIcon({ className }: { className?: string }) {
  const { themeMode } = useTheme();
  if (themeMode === 'dark') return <Moon className={className} />;
  if (themeMode === 'light') return <Sun className={className} />;
  return <Monitor className={className} />;
}

export function ThemeToggleButton() {
  const { themeMode, setThemeMode } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const close = useCallback(() => {
    setIsOpen(false);
    setFocusedIndex(-1);
    triggerRef.current?.focus();
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
    const currentIndex = THEME_OPTIONS.findIndex((o) => o.mode === themeMode);
    setFocusedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [themeMode]);

  // フォーカス管理
  useEffect(() => {
    if (isOpen && focusedIndex >= 0) {
      itemRefs.current[focusedIndex]?.focus();
    }
  }, [isOpen, focusedIndex]);

  // キーボード操作（メニューが開いているときのみ）
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault();
          close();
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev <= 0 ? THEME_OPTIONS.length - 1 : prev - 1));
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev >= THEME_OPTIONS.length - 1 ? 0 : prev + 1));
          break;
        case 'Tab':
          close();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
      if (!isOpen) {
        e.preventDefault();
        open();
      }
    }
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (isOpen ? close() : open())}
        onKeyDown={handleTriggerKeyDown}
        className="p-2 text-muted-foreground hover:text-sidebar-foreground hover:bg-sidebar-accent rounded-lg transition-colors"
        aria-label="テーマを切替"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <CurrentIcon className="w-5 h-5" />
      </button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-40 cursor-default"
            onClick={close}
            tabIndex={-1}
            aria-hidden="true"
          />
          <div
            ref={menuRef}
            role="listbox"
            aria-label="テーマ選択"
            className="absolute bottom-full left-0 mb-1 z-50 w-40 rounded-lg border border-border bg-popover py-1 shadow-lg"
          >
            {THEME_OPTIONS.map(({ mode, label, icon: Icon }, index) => (
              <button
                key={mode}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                type="button"
                role="option"
                aria-selected={themeMode === mode}
                onClick={() => {
                  setThemeMode(mode);
                  close();
                }}
                className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                  themeMode === mode
                    ? 'bg-accent text-accent-foreground'
                    : 'text-popover-foreground hover:bg-accent hover:text-accent-foreground'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
